import { convertFromRaw } from 'draft-js'
import { EditorContent, ICellValue, IFile, ITableViewColumn } from 'types'
import { DATE_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { isDate } from 'helpers/date'

// Cached Intl.NumberFormat instances for percent formatting (keyed by "locale|decimalPlaces")
const percentFormatCache: Record<string, Intl.NumberFormat> = {}

export const formatPercentDisplayValue = (value: number, locale = 'en-GB', decimalPlaces = 0): string => {
  const clampedPlaces = decimalPlaces < 0 ? 0 : decimalPlaces
  const key = `${locale}|${clampedPlaces}`
  if (!percentFormatCache[key]) {
    percentFormatCache[key] = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: clampedPlaces,
      maximumFractionDigits: clampedPlaces
    })
  }
  return percentFormatCache[key].format(value)
}

const THOUSAND_SEP_RE = /\B(?=(\d{3})+(?!\d))/g

/**
 * Group the integer part of an already-formatted number string into thousands.
 *
 * Operates on the string (not a number) so trailing zeros produced by `toFixed`
 * survive: "12345.60" -> "12,345.60", where `numberWithCommas(12345.6)` would
 * drop to "12,345.6". Shared by the spreadsheet number cell and the chart
 * labels so a value reads the same in the grid and in a chart (#10734).
 */
export const addThousandSeparator = (value: string): string => {
  const [beforeDecimal, afterDecimal] = value.split('.')
  const grouped = beforeDecimal.replace(THOUSAND_SEP_RE, ',')
  return afterDecimal !== undefined ? `${grouped}.${afterDecimal}` : grouped
}

export const numberWithCommas = (value: number | null): string => {
  if (value !== null) {
    return addThousandSeparator(value.toString())
  } else {
    return ''
  }
}

export const calculatePercentage = (decimal: number): number => {
  return Math.round(decimal * 100)
}

export const formatPercentage = (pct: number | null, decimalPlaces?: number): string => {
  if (pct !== null) {
    return pct.toFixed(decimalPlaces ? decimalPlaces : 0) + '%'
  } else {
    return ''
  }
}

export const neatRound = (number: number): number => {
  return Math.round((number + Number.EPSILON) * 100) / 100
}

function isDateOnlyLike(s: string) {
  const t = s.trim()

  // pure YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return true

  // ISO midnight UTC (e.g. 2024-05-01T00:00:00.000Z)
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(?:\.0+)?Z$/i.test(t)) return true

  return false
}

function getPartsForFormatting(dateInput: string | Date | null | undefined, kind: 'date' | 'datetime') {
  // If it's a Date already, respect it as-is
  if (dateInput instanceof Date) {
    const d = dateInput
    return {
      date: d,
      useUTC: kind === 'date', // Date objects for date-only should still be treated as floating if caller passes one
      hasTime: kind === 'datetime'
    }
  }

  // Strings
  if (typeof dateInput === 'string' && dateInput) {
    const trimmed = dateInput.trim()

    // ✅ If we're formatting a DATE column, keep it timezone-agnostic
    // even if it's an ISO midnight-Z string.
    if (kind === 'date' && isDateOnlyLike(trimmed)) {
      const [y, m, d] = trimmed.slice(0, 10).split('-').map(Number)
      const utc = new Date(Date.UTC(y, m - 1, d))
      return { date: utc, useUTC: true, hasTime: false }
    }

    // Otherwise treat as a real datetime (local TZ semantics)
    const parsed = new Date(trimmed)
    return { date: parsed, useUTC: false, hasTime: true }
  }

  // Fallback: now
  return { date: new Date(), useUTC: kind === 'date', hasTime: kind === 'datetime' }
}

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Cached Intl.DateTimeFormat instances (lazily created, keyed by UTC vs local)
const weekdayFormatCache: Record<string, { long: Intl.DateTimeFormat; short: Intl.DateTimeFormat }> = {}

function getWeekdayFormatters(useUTC: boolean) {
  const key = useUTC ? 'utc' : 'local'
  if (!weekdayFormatCache[key]) {
    const tz = useUTC ? 'UTC' : undefined
    weekdayFormatCache[key] = {
      long: new Intl.DateTimeFormat('default', { weekday: 'long', timeZone: tz }),
      short: new Intl.DateTimeFormat('default', { weekday: 'short', timeZone: tz })
    }
  }
  return weekdayFormatCache[key]
}

function formatToken(date: Date, token: string, kind: 'date' | 'datetime', useUTC: boolean) {
  const year = useUTC ? date.getUTCFullYear() : date.getFullYear()
  const monthIx = useUTC ? date.getUTCMonth() : date.getMonth()
  const day = useUTC ? date.getUTCDate() : date.getDate()

  const hours24 = useUTC ? date.getUTCHours() : date.getHours()
  const hours12 = hours24 % 12 || 12
  const minutes = useUTC ? date.getUTCMinutes() : date.getMinutes()
  const seconds = useUTC ? date.getUTCSeconds() : date.getSeconds()

  switch (token) {
    case 'YYYY':
      return String(year)
    case 'YYY':
      return String(year)
    case 'YY':
      return String(year).slice(-2)
    case 'Y':
      return String(year)

    case 'MMMM':
      return MONTHS_LONG[monthIx]
    case 'MMM':
      return MONTHS_SHORT[monthIx]
    case 'MM':
      return String(monthIx + 1).padStart(2, '0')
    case 'M':
      return String(monthIx + 1)

    case 'DDDD':
      return getWeekdayFormatters(useUTC).long.format(date)
    case 'DDD':
      return getWeekdayFormatters(useUTC).short.format(date)
    case 'DD':
      return String(day).padStart(2, '0')
    case 'D':
      return String(day)

    // Time tokens only for datetime
    case 'HH':
      return kind === 'datetime' ? String(hours24).padStart(2, '0') : ''
    case 'H':
      return kind === 'datetime' ? String(hours24) : ''
    case 'hh':
      return kind === 'datetime' ? String(hours12).padStart(2, '0') : ''
    case 'h':
      return kind === 'datetime' ? String(hours12) : ''
    case 'mm':
      return kind === 'datetime' ? String(minutes).padStart(2, '0') : ''
    case 'm':
      return kind === 'datetime' ? String(minutes) : ''
    case 'ss':
      return kind === 'datetime' ? String(seconds).padStart(2, '0') : ''
    case 's':
      return kind === 'datetime' ? String(seconds) : ''

    case 'A':
      return kind === 'datetime' ? (hours24 >= 12 ? 'PM' : 'AM') : ''
    case 'a':
      return kind === 'datetime' ? (hours24 >= 12 ? 'pm' : 'am') : ''

    case '[-/.]':
      return '-' // your chosen output delimiter
    default:
      return token
  }
}

const TOKENS_LONGEST_FIRST = [
  'YYYY',
  'YYY',
  'YY',
  'Y',
  'MMMM',
  'MMM',
  'MM',
  'M',
  'DDDD',
  'DDD',
  'DD',
  'D',
  'HH',
  'H',
  'hh',
  'h',
  'mm',
  'm',
  'ss',
  's',
  'A',
  'a',
  '[-/.]'
].sort((a, b) => b.length - a.length)

export function formatTemporal(
  dateInput: string | Date | null | undefined,
  format: string,
  kind: 'date' | 'datetime'
): string {
  const { date, useUTC } = getPartsForFormatting(dateInput, kind)

  if (Number.isNaN(date.getTime())) return ''

  let out = ''
  let i = 0

  while (i < format.length) {
    const token = TOKENS_LONGEST_FIRST.find((t) => format.startsWith(t, i))
    if (token) {
      out += formatToken(date, token, kind, useUTC)
      i += token.length
    } else {
      out += format[i]
      i++
    }
  }

  return out
}

export const formatDate = (dateInput: string, format: string) => formatTemporal(dateInput, format, 'date')

export const formatDateTime = (dateTimeInput: string | null, format: string) =>
  formatTemporal(dateTimeInput, format, 'datetime')

export const toUTCString = (localDateTime: string, kind?: string): string => {
  if (!localDateTime) return ''
  // Create a Date object from the input value
  try {
    const localDate = new Date(localDateTime)
    // Convert to UTC
    const utcDateString = localDate.toISOString().slice(0, 16)
    if (kind === 'date') return utcDateString.split('T')[0]

    return utcDateString
  } catch {
    return ''
  }
}

export const safeFormatDateISOString = (value: string, dateFormat: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return formatTemporal(date, dateFormat, 'date')
}

export const safeFormatDateTimeISOString = (value: string, dateFormat: string): string => {
  const date = new Date(value + 'Z')
  if (Number.isNaN(date.getTime())) return ''
  return formatTemporal(date, dateFormat, 'datetime')
}

// A cell value as one TSV field for the clipboard.
export const tsvEscape = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  // Arrays (multiselect, multilink) are returned as-is JSON so the clipboard contains
  // ["Yes"] rather than the TSV-mangled "[""Yes""]". The paste handler's JSON.parse
  // round-trips this correctly.
  if (Array.isArray(value)) return JSON.stringify(value)
  const raw = typeof value === 'string' ? value : JSON.stringify(value)

  // Normalize line endings inside the field
  const normalized = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  // Quote if the field contains tab, newline, or quote
  if (/[\t\n"]/.test(normalized)) {
    // Escape internal quotes by doubling them
    return `"${normalized.replace(/"/g, '""')}"`
  }
  return normalized
}

// Dates copy as the user sees them, not as the stored ISO string.
export const formatValueForCopy = (column: ITableViewColumn | undefined, value: ICellValue): ICellValue => {
  if (!column?.dateFormat || !DATE_COLUMN_TYPES.includes(column.kind)) return value

  const fmt = column.dateFormat
  return value && typeof value === 'string' && value !== '' && isDate(column.kind === 'date' ? value : value + 'Z')
    ? column.kind === 'date'
      ? safeFormatDateISOString(value, fmt)
      : safeFormatDateTimeISOString(value, fmt)
    : ''
}

export const getPlainTextDescription = (description: EditorContent) => {
  try {
    return convertFromRaw(description.content).getPlainText()
  } catch {
    return ''
  }
}

/** A duration cell's text: seconds as HH:mm, as the grid shows it. */
export const convertToDuration = (input: string, thousandSeparator: boolean) => {
  if (input === '') return ''
  const seconds = Number.parseInt(input)
  const s = Math.abs(seconds)
  const hours = Math.floor(s / 3600)
  const r = s % 3600
  const minutes = Math.floor(r / 60)

  return (
    (seconds < 0 ? '-' : '') +
    (hours < 10 ? '0' : '') +
    (thousandSeparator ? addThousandSeparator(hours.toString()) : hours) +
    ':' +
    (minutes < 10 ? '0' + minutes : minutes)
  )
}

/** A float cell's text: fixed to its decimal places, with the thousands separator if it's on. */
export const convertToFloat = (input: string, thousandSeparator: boolean, decimalPlaces: number) => {
  if (input !== undefined && input !== null && input !== '') {
    const value = parseFloat(input)
    if (thousandSeparator) {
      return addThousandSeparator(value.toFixed(decimalPlaces))
    } else {
      return value.toFixed(decimalPlaces)
    }
  } else {
    return ''
  }
}

const NUMBER_KINDS = ['integer', 'float', 'percentage', 'duration']

/** An attachment cell's text, as the grid shows it: "2 Files: plan.pdf;photo.jpg". */
export const formatAttachmentText = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) return ''
  const names = (value as (IFile | null)[]).filter((file) => file !== null).map((file) => file.filename)
  return `${value.length} File${value.length === 1 ? '' : 's'}: ${names.join(';')}`
}

/**
 * A cell's value as the grid shows it, as plain text: numbers with the column's decimal places and thousands
 * separator (percentages and durations as their cells draw them), dates in the column's format, checkboxes as
 * Yes or No, and multi-selects as a list. For text that isn't drawn by a cell, e.g. a Custom View's bound
 * values, so it matches the table.
 */
export const formatCellText = (
  column: Pick<ITableViewColumn, 'kind' | 'thousandSeparator' | 'decimalPlaces' | 'dateFormat'>,
  value: ICellValue
): string => {
  if (value === null || value === undefined || value === '') return ''
  if (NUMBER_KINDS.includes(column.kind)) {
    const number = Number(value)
    if (typeof value === 'boolean' || Number.isNaN(number)) return ''
    const raw = String(number)
    switch (column.kind) {
      case 'percentage':
        return formatPercentDisplayValue(number, 'en-GB', column.decimalPlaces ?? 0)
      case 'duration':
        return convertToDuration(String(Math.round(number)), column.thousandSeparator)
      case 'float':
        return convertToFloat(raw, column.thousandSeparator, column.decimalPlaces ?? 2)
      default:
        return column.thousandSeparator ? addThousandSeparator(raw) : raw
    }
  }
  if (column.kind === 'date' || column.kind === 'datetime') {
    if (typeof value !== 'string' || !isDate(column.kind === 'date' ? value : value + 'Z')) return ''
    return column.kind === 'date'
      ? safeFormatDateISOString(value, column.dateFormat || 'YYYY-MM-DD')
      : safeFormatDateTimeISOString(value, column.dateFormat || 'YYYY-MM-DD HH:mm:ss')
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (column.kind === 'attachment') return formatAttachmentText(value)
  if (Array.isArray(value))
    return value.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
